<script lang="ts">
  import { createEventDispatcher } from 'svelte';

  export let show = false;
  export let title = 'Confirm Action';
  export let message = 'Are you sure you want to continue?';
  export let confirmText = 'Confirm';
  export let cancelText = 'Cancel';
  export let type: 'danger' | 'warning' | 'info' = 'warning';
  export let icon = 'question circle';

  const dispatch = createEventDispatcher();

  function portal(node: HTMLElement) {
    document.body.appendChild(node);
    return {
      destroy() {
        if (node.parentNode) node.parentNode.removeChild(node);
      },
    };
  }

  function handleConfirm() {
    dispatch('confirm');
    show = false;
  }

  function handleCancel() {
    dispatch('cancel');
    show = false;
  }

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      handleCancel();
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      handleCancel();
    } else if (event.key === 'Enter') {
      handleConfirm();
    }
  }
</script>

{#if show}
  <div
    class="dialog-overlay"
    use:portal
    on:click={handleBackdropClick}
    on:keydown={handleKeydown}
    role="presentation"
  >
    <div class="dialog-container" role="dialog" aria-modal="true" aria-labelledby="dialog-title">
      <div class="dialog-header {type}">
        <div class="dialog-icon">
          <i class="{icon} icon"></i>
        </div>
        <h2 id="dialog-title" class="dialog-title">{title}</h2>
      </div>
      
      <div class="dialog-content">
        <p class="dialog-message">{message}</p>
      </div>
      
      <div class="dialog-actions">
        <button class="dialog-btn btn-cancel" on:click={handleCancel}>
          <i class="times icon"></i>
          {cancelText}
        </button>
        <button class="dialog-btn btn-confirm {type}" on:click={handleConfirm}>
          <i class="check icon"></i>
          {confirmText}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .dialog-overlay {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2147483000;
    padding: 1rem;
    animation: fadeIn 0.2s ease-out;
    background: rgba(15, 23, 42, 0.55);
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideIn {
    from {
      transform: translateY(-12px) scale(0.97);
      opacity: 0;
    }
    to {
      transform: translateY(0) scale(1);
      opacity: 1;
    }
  }

  .dialog-container {
    position: relative;
    background: white;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    max-width: 480px;
    width: calc(100% - 2rem);
    overflow: hidden;
    animation: slideIn 0.3s ease-out;
  }

  .dialog-header {
    padding: 2rem 2rem 1.5rem;
    text-align: center;
    position: relative;
  }

  .dialog-header.danger {
    background: linear-gradient(160deg, #ffe4e6 0%, #fecdd3 45%, #fda4af 100%);
  }

  .dialog-header.warning {
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  }

  .dialog-header.info {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  }

  .dialog-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    margin-bottom: 1rem;
  }

  .dialog-header.danger .dialog-icon {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
  }

  .dialog-header.warning .dialog-icon {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
  }

  .dialog-header.info .dialog-icon {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  .dialog-icon i {
    font-size: 2rem;
    color: white;
    margin: 0;
  }

  .dialog-title {
    font-size: 1.5rem;
    font-weight: 500;
    color: var(--grey);
    margin: 0;
  }

  .dialog-header.danger .dialog-title {
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
  }

  .dialog-content {
    padding: 1.5rem 2rem 2rem;
  }

  .dialog-message {
    font-size: 1rem;
    line-height: 1.6;
    color: var(--grey);
    margin: 0;
    text-align: center;
  }

  .dialog-actions {
    display: flex;
    gap: 1rem;
    padding: 0 2rem 2rem;
  }

  .dialog-btn {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.875rem 1.5rem;
    border: 2px solid;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .dialog-btn i {
    font-size: 1rem;
    margin: 0;
  }

  .btn-cancel {
    background: white;
    color: var(--grey);
    border-width: 1px;
    border-color: var(--border-color);
  }

  .btn-cancel:hover {
    background: var(--greyish);
    border-color: var(--grey-secondary);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .btn-cancel:active {
    transform: translateY(0);
  }

  .btn-confirm {
    color: white;
  }

  .btn-confirm.danger {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    border-color: #dc2626;
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
  }

  .btn-confirm.danger:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
  }

  .btn-confirm.warning {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    border-color: #d97706;
    box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
  }

  .btn-confirm.warning:hover {
    background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
  }

  .btn-confirm.info {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    border-color: #2563eb;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }

  .btn-confirm.info:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  .btn-confirm:active {
    transform: translateY(0);
  }

  /* Responsive */
  @media (max-width: 768px) {
    .dialog-container {
      max-width: calc(100vw - 2rem);
    }

    .dialog-header {
      padding: 1.5rem 1.5rem 1rem;
    }

    .dialog-icon {
      width: 56px;
      height: 56px;
      margin-bottom: 0.75rem;
    }

    .dialog-icon i {
      font-size: 1.75rem;
    }

    .dialog-title {
      font-size: 1.25rem;
    }

    .dialog-content {
      padding: 1rem 1.5rem 1.5rem;
    }

    .dialog-message {
      font-size: 0.9375rem;
    }

    .dialog-actions {
      flex-direction: column;
      padding: 0 1.5rem 1.5rem;
    }

    .dialog-btn {
      width: 100%;
      padding: 0.75rem 1.25rem;
      font-size: 0.9375rem;
    }
  }
</style>

